import { useEffect, useRef } from 'react'

/**
 * Shared modal accessibility behavior: attach the returned ref to the modal's
 * container element. While open it closes on Escape, traps Tab focus inside the
 * dialog, moves focus into the dialog on open, and restores focus to the
 * previously-focused element on close.
 */
export function useModalA11y<T extends HTMLElement = HTMLDivElement>(
  isOpen: boolean,
  onClose: () => void,
) {
  const containerRef = useRef<T | null>(null)
  const previouslyFocused = useRef<HTMLElement | null>(null)
  // Keep the latest onClose in a ref so neither effect needs it as a dependency.
  // Callers usually pass an inline arrow function, which changes identity on
  // every render - and the parent re-renders on every keystroke inside the
  // modal. If the focus-on-open effect depended on onClose it would re-run on
  // each keystroke and yank focus back to the first field (the "cursor jumps to
  // Name after typing Price" bug). Refs sidestep that entirely.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  const getFocusables = () => {
    const container = containerRef.current
    return container
      ? Array.from(
          container.querySelectorAll<HTMLElement>(
            'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
          ),
        ).filter((el) => el.offsetParent !== null || el === document.activeElement)
      : []
  }

  // Move focus into the dialog EXACTLY ONCE per open, and restore on close.
  // Depends only on isOpen, so it fires on the open transition and never again
  // while the modal stays open.
  useEffect(() => {
    if (!isOpen) return undefined
    previouslyFocused.current = document.activeElement as HTMLElement | null
    const first = getFocusables()[0]
    if (first) first.focus()
    else containerRef.current?.focus()
    return () => {
      // Restore focus to whatever was focused before the modal opened.
      previouslyFocused.current?.focus?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // Escape-to-close + Tab focus trap. Re-attaching the listener is harmless, and
  // it reads onClose via the ref so it stays out of the deps.
  useEffect(() => {
    if (!isOpen) return undefined
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onCloseRef.current()
        return
      }
      if (e.key === 'Tab') {
        const items = getFocusables()
        if (items.length === 0) return
        const firstEl = items[0]
        const lastEl = items[items.length - 1]
        if (e.shiftKey && document.activeElement === firstEl) {
          e.preventDefault()
          lastEl.focus()
        } else if (!e.shiftKey && document.activeElement === lastEl) {
          e.preventDefault()
          firstEl.focus()
        }
      }
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  return containerRef
}
